import { apiFetch } from '@/lib/api/http';

/**
 * 認証系の型。M1 で確定した API 契約に対応する。
 * `make gen-types` の生成型（schema.d.ts）へ順次置き換える（指示書 §0-5 / DECISIONS D-004）。
 */
export type Role = 'student' | 'director' | 'success' | 'office' | 'super_admin';
export type OfficeGrade = 'office_admin' | 'office_member';

export interface RoleInfo {
  role: Role;
  office_grade: OfficeGrade | null;
}

export interface MemberSummary {
  id: string;
  member_id: string;
  name: string;
}

export interface CurrentUser {
  id: string;
  email: string;
  display_name: string | null;
  /** 受講生以外のプロフィール（D-081・D-082）。受講生の分は member 側にある */
  photo_url: string | null;
  bio: string | null;
  company: string | null;
  position: string | null;
  phone: string | null;
  /** ログイン用の email とは別の連絡先 */
  contact_email: string | null;
  roles: RoleInfo[];
  active_role: Role;
  office_grade: OfficeGrade | null;
  totp_required: boolean;
  totp_verified: boolean;
  totp_enrolled: boolean;
  /** パスワードを設定済みか（事務局・スーパー管理者は任意 / D-068） */
  has_password: boolean;
  member: MemberSummary | null;
  last_login_at: string | null;
}

export interface LoginResult {
  user: CurrentUser;
  csrf_token: string;
}

export interface TotpSetup {
  secret: string;
  provisioning_uri: string;
  backup_codes: string[];
}

export const ROLE_LABELS: Record<Role, string> = {
  student: '受講生',
  director: '理事・講師',
  success: 'サクセス担当',
  office: '事務局',
  super_admin: 'スーパー管理者',
};

export function fetchMe(): Promise<CurrentUser> {
  return apiFetch<CurrentUser>('/auth/me');
}

export function login(email: string, password: string): Promise<LoginResult> {
  return apiFetch<LoginResult>('/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
}

export function loginWithGoogleMock(email: string): Promise<LoginResult> {
  return apiFetch<LoginResult>('/auth/google/mock', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

export function logout(): Promise<void> {
  return apiFetch<void>('/auth/logout', { method: 'POST' });
}

export function switchRole(role: Role): Promise<CurrentUser> {
  return apiFetch<CurrentUser>('/auth/active-role', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
}

export function setupTotp(): Promise<TotpSetup> {
  return apiFetch<TotpSetup>('/auth/totp/setup', { method: 'POST' });
}

export function confirmTotp(code: string): Promise<CurrentUser> {
  return apiFetch<CurrentUser>('/auth/totp/confirm', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
}

export function verifyTotp(code: string): Promise<CurrentUser> {
  return apiFetch<CurrentUser>('/auth/totp/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
}

export function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return apiFetch<void>('/auth/password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
}

/** 編集できるプロフィール項目（本人・事務局の代理編集で共通 / D-081・D-082） */
export interface ProfileInput {
  display_name?: string | null;
  bio?: string | null;
  company?: string | null;
  position?: string | null;
  phone?: string | null;
  contact_email?: string | null;
}

/** 自分のプロフィールを更新する（受講生以外 / D-081） */
export function updateOwnProfile(input: ProfileInput): Promise<CurrentUser> {
  return apiFetch<CurrentUser>('/auth/profile', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function uploadOwnProfilePhoto(file: File): Promise<CurrentUser> {
  const body = new FormData();
  body.append('file', file);
  return apiFetch<CurrentUser>('/auth/profile/photo', { method: 'PUT', body });
}

export function deleteOwnProfilePhoto(): Promise<CurrentUser> {
  return apiFetch<CurrentUser>('/auth/profile/photo', { method: 'DELETE' });
}

/** 再設定メールの送信を依頼する（未ログインで使う。存在しないメールでも成功扱い） */
export function requestPasswordReset(email: string): Promise<void> {
  return apiFetch<void>('/auth/password/reset-request', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

/** メールのリンクに含まれるトークンで新しいパスワードを設定する */
export function resetPassword(token: string, newPassword: string): Promise<void> {
  return apiFetch<void>('/auth/password/reset', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
}

export function verifyBackupCode(code: string): Promise<CurrentUser> {
  return apiFetch<CurrentUser>('/auth/totp/backup-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
}
